import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload as UploadIcon, FileText, Calendar, Clock, X, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { parseTranscriptText } from '@/utils/parser';
import { useTranscriptStore } from '@/store';

export function Upload() {
  const navigate = useNavigate();
  const setTranscript = useTranscriptStore((state) => state.setTranscript);
  
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [recordingDate, setRecordingDate] = useState(
    new Date().toISOString().slice(0, 16)
  );
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'text/plain') {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setText(content);
        if (!title) {
          setTitle(file.name.replace(/\.[^.]+$/, ''));
        }
      };
      reader.readAsText(file);
    }
  }, [title]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setText(content);
        if (!title) {
          setTitle(file.name.replace(/\.[^.]+$/, ''));
        }
      };
      reader.readAsText(file);
    }
  }, [title]);

  const handleSubmit = useCallback(() => {
    if (!text.trim()) {
      setError('Please paste or upload a transcript');
      return;
    }

    try {
      const transcript = parseTranscriptText(text, {
        title: title || 'Untitled Transcript',
        recordingDate: new Date(recordingDate),
      });

      if (transcript.segments.length === 0) {
        setError('Could not parse any segments from the transcript. Check the format.');
        return;
      }

      setTranscript(transcript);
      navigate('/');
    } catch (err) {
      setError(`Failed to parse transcript: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [text, title, recordingDate, setTranscript, navigate]);

  const loadSampleData = useCallback(() => {
    const sampleTranscript = `[00:00.000 → 00:10.960] **SPEAKER_02:** Let's go without your backpack and it's all late.
[00:10.960 → 00:20.960] **Speaker ?:** I will go with my backpack. As you like.
[00:40.960 → 02:27.960] **Speaker ?:** I will go with my backpack. I will go with my backpack. I will go with my backpack. I will go with my backpack. Yes? What?
[02:28.960 → 02:35.960] **SPEAKER_05:** Panagiotis? Panagiotis? I am Jose.`;
    
    setText(sampleTranscript);
    setTitle('Sample Conversation');
  }, []);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
          Import Transcript
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          Paste your transcript or drop a text file to visualize it
        </p>
      </div>

      {/* Error Banner */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3"
          >
            <div className="flex-1 text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700 dark:hover:text-red-300"
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Metadata Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            <FileText className="w-4 h-4 inline-block mr-2" />
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Meeting with Team"
            className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all text-slate-900 dark:text-white placeholder-slate-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            <Calendar className="w-4 h-4 inline-block mr-2" />
            Recording Date & Time
          </label>
          <input
            type="datetime-local"
            value={recordingDate}
            onChange={(e) => setRecordingDate(e.target.value)}
            className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all text-slate-900 dark:text-white"
          />
        </div>
      </div>

      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        className={`relative border-2 border-dashed rounded-2xl p-6 transition-all ${
          isDragging
            ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20'
            : 'border-slate-300 dark:border-slate-600 hover:border-sky-400'
        }`}
      >
        <div className="text-center mb-4">
          <UploadIcon className={`w-10 h-10 mx-auto mb-3 ${isDragging ? 'text-sky-500' : 'text-slate-400'}`} />
          <p className="text-slate-600 dark:text-slate-400 text-sm">
            Drag & drop a text file, or{' '}
            <label className="text-sky-600 dark:text-sky-400 cursor-pointer hover:underline">
              browse
              <input
                type="file"
                accept=".txt"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          </p>
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`Paste your transcript here...

Example format:
[00:00.000 → 00:10.960] **SPEAKER_02:** Hello everyone
[00:10.960 → 00:20.960] **Speaker ?:** Hi there!`}
          className="w-full h-48 px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all text-sm font-mono text-slate-900 dark:text-white placeholder-slate-400 resize-none"
        />
      </div>

      {/* Sample Data Button */}
      <div className="flex justify-center">
        <button
          onClick={loadSampleData}
          className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
        >
          <Sparkles className="w-4 h-4" />
          Load sample transcript
        </button>
      </div>

      {/* Format Guide */}
      <div className="bg-slate-100 dark:bg-slate-800/50 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
          <Clock className="w-4 h-4" />
          Supported Formats
        </h3>
        <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
          <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">[00:00.000 → 00:10.960] **SPEAKER:** Text</code></li>
          <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">[00:00 - 00:10] Speaker Name: Text</code></li>
          <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">00:00.000 - 00:10.960 | SPEAKER: Text</code></li>
        </ul>
      </div>

      {/* Submit Button */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleSubmit}
        disabled={!text.trim()}
        className="w-full py-4 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 disabled:from-slate-300 disabled:to-slate-400 dark:disabled:from-slate-600 dark:disabled:to-slate-700 text-white font-semibold rounded-xl shadow-lg shadow-sky-200 dark:shadow-sky-900/30 disabled:shadow-none transition-all"
      >
        Visualize Transcript
      </motion.button>
    </div>
  );
}
